import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faListCheck, faInbox, faArrowsRotate, faCircleCheck, faCommentDots, faComments,
    faUsers, faPhone, faPhoneSlash, faPlaneDeparture, faCalendarCheck, faReceipt,
    faFileInvoiceDollar, faChevronLeft, faChevronRight, faWaveSquare, faFolderOpen,
    faRightToBracket, faRightFromBracket, faHourglassHalf, faVideo, faVideoSlash, faCalendarPlus,
    faUmbrellaBeach, faMugHot, faHouseLaptop, faUserXmark, faUserCheck, faClock
} from '@fortawesome/free-solid-svg-icons';

import api from '../utils/api';
import { officeToday, monthGrid, shiftMonth, monthKey, longDay, officeTime } from '../utils/personActivity';
import '../styles/personActivity.css';

/**
 * One person's activity, a day at a time: the calendar beside the list, so
 * picking a day never means scrolling down to find it. Days with something
 * on them are marked; arrows move a month back or forward.
 *
 * Above the day's list, what kind of day it was for them: a holiday, a
 * Sunday, on leave, working from home, and how their attendance went. The
 * calendar colours the same days, so leave and holidays show at a glance.
 *
 * What the rows say comes from the server (server/utils/personActivity.js).
 * A task opens in the page's task drawer; leave, expenses and attendance open
 * the profile's own tabs for them.
 */

const LOOK = {
    task_created:      { icon: faListCheck,         tone: 'task' },
    task_assigned:     { icon: faInbox,             tone: 'task' },
    task_status:       { icon: faArrowsRotate,      tone: 'task' },
    task_completed:    { icon: faCircleCheck,       tone: 'done' },
    task_comment:      { icon: faCommentDots,       tone: 'chat' },
    discussion:        { icon: faComments,          tone: 'chat' },
    group_created:     { icon: faUsers,             tone: 'chat' },
    call:              { icon: faPhone,             tone: 'call' },
    call_missed:       { icon: faPhoneSlash,        tone: 'missed' },
    leave_applied:     { icon: faPlaneDeparture,    tone: 'leave' },
    leave_decided:     { icon: faCalendarCheck,     tone: 'leave' },
    expense_submitted: { icon: faReceipt,           tone: 'expense' },
    expense_decided:   { icon: faFileInvoiceDollar, tone: 'expense' },
    checked_in:        { icon: faRightToBracket,    tone: 'done' },
    checked_out:       { icon: faRightFromBracket,  tone: 'muted' },
    short_leave:       { icon: faHourglassHalf,     tone: 'leave' },
    meeting:           { icon: faVideo,             tone: 'call' },
    meeting_missed:    { icon: faVideoSlash,        tone: 'missed' },
    meeting_scheduled: { icon: faCalendarPlus,      tone: 'call' }
};

// The banners that say what kind of day it was.
const DAY_LOOK = {
    holiday:       { icon: faUmbrellaBeach, tone: 'holiday' },
    sunday:        { icon: faMugHot,        tone: 'sunday' },
    leave:         { icon: faPlaneDeparture, tone: 'leave' },
    leave_pending: { icon: faHourglassHalf, tone: 'leave' },
    wfh:           { icon: faHouseLaptop,   tone: 'wfh' },
    absent:        { icon: faUserXmark,     tone: 'absent' },
    attendance:    { icon: faUserCheck,     tone: 'present' }
};

const MARK_NAMES = { holiday: 'Holiday', leave: 'On leave', absent: 'Absent', wfh: 'Work from home' };

const FILTERS = [
    { key: 'all', label: 'Everything', kinds: null },
    { key: 'tasks', label: 'Tasks', kinds: ['task_created', 'task_assigned', 'task_status', 'task_completed'] },
    { key: 'talk', label: 'Discussions', kinds: ['task_comment', 'discussion', 'group_created'] },
    { key: 'attendance', label: 'Attendance', kinds: ['checked_in', 'checked_out', 'short_leave'] },
    { key: 'calls', label: 'Calls', kinds: ['call', 'call_missed'] },
    { key: 'meetings', label: 'Meetings', kinds: ['meeting', 'meeting_missed', 'meeting_scheduled'] },
    { key: 'leave', label: 'Leave', kinds: ['leave_applied', 'leave_decided'] },
    { key: 'expenses', label: 'Expenses', kinds: ['expense_submitted', 'expense_decided'] }
];

// The day last looked at per person, so going to Leaves and back lands on
// the same day rather than today.
const lastDay = new Map();

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PersonActivity = ({ userId, onOpenTask, onOpenTab }) => {
    const today = officeToday();
    const [day, setDay] = useState(() => lastDay.get(userId) || today);
    const [shown, setShown] = useState(() => ({ year: +day.slice(0, 4), month: +day.slice(5, 7) }));
    const [counts, setCounts] = useState({});
    const [marks, setMarks] = useState({});
    const [items, setItems] = useState([]);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState('all');

    const month = monthKey(shown);
    const thisMonth = today.slice(0, 7);

    useEffect(() => {
        let live = true;
        setCounts({});
        setMarks({});
        api.get(`/employees/${userId}/activity/month`, { params: { month } })
            .then(({ data }) => {
                if (!live) return;
                setCounts(data?.days || {});
                setMarks(data?.marks || {});
            })
            .catch(() => {});
        return () => { live = false; };
    }, [userId, month]);

    useEffect(() => {
        let live = true;
        setLoading(true);
        setFailed(false);
        api.get(`/employees/${userId}/activity`, { params: { date: day } })
            .then(({ data }) => {
                if (!live) return;
                setItems(data?.items || []);
                setBanners(data?.day || []);
            })
            .catch(() => { if (live) { setItems([]); setBanners([]); setFailed(true); } })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [userId, day]);

    const weeks = useMemo(() => monthGrid(shown.year, shown.month), [shown]);
    const visible = useMemo(() => {
        const kinds = FILTERS.find((f) => f.key === filter)?.kinds;
        return kinds ? items.filter((i) => kinds.includes(i.kind)) : items;
    }, [items, filter]);

    const open = (item) => {
        const t = item.target;
        if (!t) return;
        if (t.type === 'task') onOpenTask?.(t.id);
        else if (t.type === 'leave') onOpenTab?.('leaves');
        else if (t.type === 'expense') onOpenTab?.('expenses');
        else if (t.type === 'attendance') onOpenTab?.('attendance');
    };
    const clickable = (item) => ['task', 'leave', 'expense', 'attendance'].includes(item.target?.type);
    const isSunday = (d) => new Date(`${d}T12:00:00Z`).getUTCDay() === 0;

    const pick = (d) => {
        lastDay.set(userId, d);
        setDay(d);
        setFilter('all');
    };

    return (
        <div className="pa-wrap fade-in">
            <aside className="pa-cal control-card" aria-label="Pick a day">
                <div className="pa-cal-head">
                    <button type="button" className="pa-cal-nav" onClick={() => setShown((s) => shiftMonth(s, -1))} aria-label="Previous month">
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <strong>
                        {new Date(Date.UTC(shown.year, shown.month - 1, 15)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })}
                    </strong>
                    <button
                        type="button"
                        className="pa-cal-nav"
                        onClick={() => setShown((s) => shiftMonth(s, 1))}
                        disabled={month >= thisMonth}
                        aria-label="Next month"
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>

                <div className="pa-cal-grid" role="grid">
                    {WEEKDAYS.map((w) => <div key={w} className="pa-cal-wd">{w}</div>)}
                    {weeks.flat().map((d, i) => {
                        if (!d) return <div key={`pad-${i}`} />;
                        const n = counts[d] || 0;
                        const future = d > today;
                        const mark = marks[d];
                        return (
                            <button
                                key={d}
                                type="button"
                                className={[
                                    'pa-cal-day',
                                    d === day && 'is-selected',
                                    d === today && 'is-today',
                                    n > 0 && 'has-activity',
                                    mark && `is-${mark}`,
                                    isSunday(d) && 'is-sunday'
                                ].filter(Boolean).join(' ')}
                                disabled={future && !mark}
                                onClick={() => pick(d)}
                                title={[MARK_NAMES[mark] || (isSunday(d) ? 'Sunday' : ''), n ? `${n} thing${n === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ') || 'Nothing recorded'}
                                aria-pressed={d === day}
                            >
                                <span>{+d.slice(8)}</span>
                                {n > 0 && <i className="pa-cal-mark" />}
                            </button>
                        );
                    })}
                </div>

                <div className="pa-legend" aria-hidden="true">
                    <span><i className="pa-key is-leave" /> Leave</span>
                    <span><i className="pa-key is-holiday" /> Holiday</span>
                    <span><i className="pa-key is-absent" /> Absent</span>
                    <span><i className="pa-key is-wfh" /> Home</span>
                    <span><i className="pa-key-dot" /> Activity</span>
                </div>

                {day !== today && (
                    <button
                        type="button"
                        className="pa-cal-today"
                        onClick={() => { pick(today); setShown({ year: +today.slice(0, 4), month: +today.slice(5, 7) }); }}
                    >
                        Back to today
                    </button>
                )}
            </aside>

            <section className="pa-day control-card">
                <div className="pa-day-head">
                    <div>
                        <h3>{day === today ? 'Today' : longDay(day)}</h3>
                        <span className="pa-day-sub">
                            {day === today ? longDay(day) : ''}
                            {!loading && !failed && ` ${day === today ? '· ' : ''}${items.length} thing${items.length === 1 ? '' : 's'}`}
                        </span>
                    </div>
                </div>

                {!loading && !failed && banners.length > 0 && (
                    <div className="pa-banners">
                        {banners.map((b) => {
                            const look = DAY_LOOK[b.kind] || { icon: faClock, tone: 'present' };
                            return (
                                <div key={b.kind} className={`pa-banner ${look.tone}`}>
                                    <FontAwesomeIcon icon={look.icon} className="pa-banner-icon" />
                                    <div>
                                        <strong>{b.title}</strong>
                                        {b.text && <span>{b.text}</span>}
                                        {b.detail && <em>“{b.detail}”</em>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {items.length > 0 && (
                    <div className="pa-filters">
                        {FILTERS.map((f) => {
                            const n = f.kinds ? items.filter((i) => f.kinds.includes(i.kind)).length : items.length;
                            if (f.kinds && !n) return null;
                            return (
                                <button
                                    key={f.key}
                                    type="button"
                                    className={`pa-chip ${filter === f.key ? 'is-active' : ''}`}
                                    onClick={() => setFilter(f.key)}
                                >
                                    {f.label} <span>{n}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {loading ? (
                    <div className="pa-empty">Loading activity…</div>
                ) : failed ? (
                    <div className="pa-empty">Could not load this day. Try again in a moment.</div>
                ) : visible.length === 0 && banners.length > 0 ? (
                    <div className="pa-empty is-quiet">No other activity on this day.</div>
                ) : visible.length === 0 ? (
                    <div className="pa-empty">
                        <FontAwesomeIcon icon={faWaveSquare} className="pa-empty-icon" />
                        <strong>Nothing recorded on this day</strong>
                        Days with activity have a dot in the calendar.
                    </div>
                ) : (
                    <ol className="pa-feed">
                        {visible.map((item) => {
                            const look = LOOK[item.kind] || { icon: faWaveSquare, tone: 'task' };
                            const canOpen = clickable(item);
                            const Row = canOpen ? 'button' : 'div';
                            return (
                                <li key={item.id}>
                                    <Row
                                        {...(canOpen ? { type: 'button', onClick: () => open(item) } : {})}
                                        className={`pa-row ${canOpen ? 'is-clickable' : ''}`}
                                    >
                                        <span className={`pa-icon ${look.tone}`}>
                                            <FontAwesomeIcon icon={look.icon} />
                                        </span>
                                        <span className="pa-body">
                                            <span className="pa-text">{item.text}</span>
                                            {item.detail && <span className="pa-detail">“{item.detail}”</span>}
                                            <span className="pa-meta">
                                                <time dateTime={item.at}>{officeTime(item.at)}</time>
                                                {item.project?.name && (
                                                    <span className="pa-project">
                                                        <FontAwesomeIcon icon={faFolderOpen} /> {item.project.name}
                                                    </span>
                                                )}
                                            </span>
                                        </span>
                                    </Row>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </section>
        </div>
    );
};

export default PersonActivity;
